/** @jsxImportSource react */
import clsxm from '@common/lib/clsxm';
import { Button, Link } from '@react/system';
import { Agent, GeoGuide } from '@sly/core/api/types';
import {
  ASSISTED_LIVING,
  INDEPENDENT_LIVING,
  MEMORY_CARE,
  SKILLED_NURSING_FACILITY,
} from '@sly/core/constants/tags';
import { humanizeList } from '@sly/core/helpers/array';
import { formatMoney, formatNumber } from '@sly/core/helpers/numbers';
import { useState } from 'react';

interface SummaryProps {
  geoGuide: GeoGuide;
  agents: Agent[];
  communitiesCount: number;
}

const templateText: Record<string, string> = {
  [ASSISTED_LIVING]:
    "Not to be confused with nursing homes, which offer medical care, assisted living provides daily help with activities of daily living like dressing or bathing. And with services catering to all care levels, many facilities enable residents to age in place, whether it's a 1 bedroom or 2 bedroom option.",
  [INDEPENDENT_LIVING]: `If you're looking for modern amenities, keep an eye out for our "new construction" badge which will help you find the newest facilities in your area. The new age of retirement living is here, with so many options for independent seniors. From all-inclusive retirement communities to luxury facilities for those who want to stay active, we've got the most comprehensive set of options. Many of these facilities also offer assisted living and memory care, so if you're looking for a community that allows you to age in place, there are plenty of choices.`,
  [MEMORY_CARE]:
    "Finding the right memory care facility for a loved one with Alzheimer's or dementia is a complex journey. Memory care communities offer support with activities of daily living as well as features designed specifically for residents with memory challenges.From staff that are specially trained to manage complex behaviors like agitation or sundowning to secure units that are designed to prevent wandering, finding the right memory care facility can make a big difference.",
  [SKILLED_NURSING_FACILITY]: `We also review and summarize important information from CMS to help you understand how staffing, inspections, and other quality measures impact care at each one of these nursing homes. Pay attention to the Seniorly Score, our proprietary rating system for nursing homes. Nursing homes, sometimes called skilled nursing facilities,
deliver the highest level of care in the senior living landscape. Skilled nursing facilities (SNFs) provide critical care and rehabilitation for individuals recovering from surgery, illness, or injury.
Offering 24/7 medical supervision, these facilities feature specialized teams of therapists, doctors, nurses dedicated to restoring patients' independence.
Whether you're facing a complex diagnosis ,or need rehab or post-surgical care, an SNF can be a vital step on the road to recovery.`,
};

const getPricingSentence = (geoGuide: GeoGuide) => {
  const dataPoints = geoGuide.guideContent?.curated?.dataPoints;

  if (!dataPoints) {
    return null;
  }

  if (geoGuide?.info?.pricingStats?.aiPrices?.small?.privateRoom?.value) {
    return (
      <>
        The average cost of {geoGuide.caretype.toLowerCase()} living in the{' '}
        {geoGuide.city} area is{' '}
        {formatMoney(
          geoGuide?.info?.pricingStats?.aiPrices?.small?.privateRoom?.value
        )}
        , and the communities in the {geoGuide.city} area have a average rating
        of {(dataPoints.averageSeniorlyScore / 10).toFixed(1)} out of 10.
      </>
    );
  }

  return (
    <>
      The communities in the {geoGuide.city} area have a average rating of{' '}
      {(dataPoints.averageSeniorlyScore / 10).toFixed(1)} out of 10.
    </>
  );
};

export const Summary = ({
  geoGuide,
  agents,
  communitiesCount,
}: SummaryProps) => {
  const [readMore, setReadMore] = useState(false);

  const dataPoints = geoGuide.guideContent?.curated?.dataPoints;

  if (!geoGuide.guideContent?.curated || !dataPoints) {
    return null;
  }

  return (
    <>
      <div
        className={clsxm(
          'line-clamp-8 md:line-clamp-none',
          readMore && 'line-clamp-none',
          '[&_a]:text-viridian-base [&_a:hover]:underline'
        )}
      >
        {geoGuide.city} offers a diverse range of{' '}
        {formatNumber(communitiesCount)} {geoGuide.caretype} communities giving
        families varied options to choose from. {getPricingSentence(geoGuide)}{' '}
        Seniorly also uses{' '}
        <a href='https://www.seniorly.com/senior-living-pricing-trends'>
          Seniorly Price Estimate{' '}
        </a>
        for cost comparisons with nearby cities, and even highlights new
        construction facilities for those seeking modern amenities. Families can
        look at {formatNumber(dataPoints.photos)} photos,{' '}
        {formatNumber(dataPoints.reviews)} ratings and reviews, and{' '}
        {formatNumber(dataPoints.prices)} prices in the {geoGuide.city} area to
        get a sense of {geoGuide.caretype.toLowerCase()} in the area.{' '}
        {templateText[geoGuide.caretype]} For personalized assistance, Seniorly
        local advisors{' '}
        {humanizeList(
          agents?.map((a) => (
            <Link key={a.info.displayName} href={a.info.url}>
              {a.info.displayName}
            </Link>
          ))
        )}
        &nbsp;are available to help with your search.
      </div>
      <Button
        onClick={() => setReadMore(!readMore)}
        className='mt-6 no-underline md:hidden'
        variant='underlined'
      >
        Read more
      </Button>
    </>
  );
};
